import { useEffect, useRef } from "react";
import { me as apiMe, refresh as apiRefresh } from "@/features/auth/api";
import { api, buildStorefrontApiPath } from "@/lib/api";
import { useAuthStore } from "@/store/auth";

const NEW_AUTH_STORAGE_KEY = "kwad.storefront.auth";
const LEGACY_AUTH_STORAGE_KEY = "auth";

function hasStoredAuthArtifacts() {
  try {
    return Boolean(
      window.localStorage.getItem(NEW_AUTH_STORAGE_KEY) ||
        window.localStorage.getItem(LEGACY_AUTH_STORAGE_KEY),
    );
  } catch {
    return false;
  }
}

export function useAuthBootstrap() {
  const ranRef = useRef(false);

  const accessToken = useAuthStore((s) => s.accessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const setUser = useAuthStore((s) => s.setUser);
  const setOrganization = useAuthStore((s) => s.setOrganization);
  const setCsrfToken = useAuthStore((s) => s.setCsrfToken);
  const logoutLocal = useAuthStore((s) => s.logoutLocal);
  const markHydrated = useAuthStore((s) => s.markHydrated);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        if (accessToken) {
          try {
            const result = await apiMe();
            if (cancelled) return;

            setUser(result.customer);
            setOrganization(result.organization);

            // Lane 2.5.A — el csrfToken no se persiste. Si /me tuvo éxito
            // en hydration, pedimos un token fresco a /auth/csrf para
            // habilitar mutaciones sin incurrir en el 403-recovery path.
            try {
              const res = await api.get(
                buildStorefrontApiPath("/auth/csrf"),
              );
              const payload = (res.data?.data ?? res.data ?? {}) as {
                csrfToken?: string | null;
              };
              if (!cancelled && payload.csrfToken) {
                setCsrfToken(payload.csrfToken);
              }
            } catch {
              // fail-soft: el response interceptor recuperará en la
              // primera mutación vía GET /auth/csrf + retry.
            }

            return;
          } catch {
            // Si falla /me, seguimos a refresh sólo si hay rastro real de sesión
          }
        }

        const shouldAttemptRefresh = hasStoredAuthArtifacts();

        if (!shouldAttemptRefresh) {
          logoutLocal();
          return;
        }

        let session = null;
        try {
          session = await apiRefresh();
        } catch {
          session = null;
        }

        if (cancelled) return;

        if (session) {
          setSession(session);
        } else {
          logoutLocal();
        }
      } catch {
        if (!cancelled) {
          logoutLocal();
        }
      } finally {
        if (!cancelled) {
          markHydrated();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    logoutLocal,
    markHydrated,
    setCsrfToken,
    setOrganization,
    setSession,
    setUser,
  ]);
}